import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/confirmation';
import { OptionBoxDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/optionbox';
import { WaitModalComponent } from '../modals/wait-modal/wait-modal.component';
import { UserInfo } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AppService } from '../../services/app.service';
import { ErrorService } from '../../services/error.service';
import { ScreenSizeService } from '@polk_county_public_schools/ngx-pcps-dialogs/core';
import { EnvironmentService } from '../../services/environment.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    imports: [
        MatCheckboxModule,
        MatRadioModule,
        ReactiveFormsModule,
        MatInputModule,
        MatToolbarModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        RouterModule
    ],
    providers: [
        OptionBoxDialogService
    ]
})
export class LoginComponent implements OnInit {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly appSvc = inject(AppService);
    protected readonly authSvc = inject(AuthService);
    protected readonly confDlgSvc = inject(ConfirmationDialogService);
    protected readonly matDialog = inject(MatDialog);
    protected readonly optionDlgSvc = inject(OptionBoxDialogService);
    protected readonly errorSvc = inject(ErrorService);
    protected readonly router = inject(Router);
    protected readonly snackBarSvc = inject(SnackBarService);
    protected readonly screenSizeSvc = inject(ScreenSizeService);
    protected readonly envSvc = inject(EnvironmentService);
    lForm: FormGroup;
    failures: number = 0;
    showAdditionalFields: boolean = false;
    smallScreen: boolean;
    isDevMode: boolean;

    ngOnInit(): void {
        this.isDevMode = this.envSvc.isDevMode;

        this.lForm = new FormGroup({
            username: new FormControl('', Validators.required),
            password: new FormControl('', Validators.required),
            impersonationSelection: new FormControl('username'),
            clearTraining: new FormControl(false),
            impersonatedUsername: new FormControl('')
        });

        this.lForm.get('clearTraining').disable();

        this.lForm.get('impersonationSelection').valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(val => {
            if (val === 'username') {
                this.lForm.get('clearTraining').disable();
                this.lForm.get('impersonatedUsername').enable();
            }
            else {
                this.lForm.get('clearTraining').enable();
                this.lForm.get('impersonatedUsername').disable();
            }
        });

        if (this.screenSizeSvc != null) {
            this.screenSizeSvc.ScreenSizeSub
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((size) => {
                    if (size == null) return;
                    this.smallScreen = size.IsSm || size.IsXs;
                });
        }
    }

    login(form: any) {
        if (form.invalid) {
            return;
        }

        if (form.value.username.includes('@')) {
            this.snackBarSvc.error('Do not include @ in your username', 5000);
            return;
        }

        let dialog = this.matDialog.open(WaitModalComponent);

        this.appSvc
            .ValidateUser(
                form.value.username,
                form.value.password,
                form.value.impersonationSelection,
                form.value.clearTraining,
                form.value.impersonatedUsername
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (info: UserInfo) => {
                    this.authSvc.ProcessLogin(info);
                    dialog.close();
                },
                error: (error: any) => {
                    if (error.error === 'Student not eligible to apply') {
                        this.router.navigate(['instructions']);
                    }

                    this.invalidLogin(error);
                    dialog.close();
                }
            });
    }

    impersonation($event: MouseEvent) {
        if (!$event) return;

        if ($event.ctrlKey === true) {
            this.showAdditionalFields = true;
        }
    }

    invalidLogin(error: any) {
        this.errorSvc.HandleServiceError(error);

        if (error.status === 401) {
            // Unauthorized. Typically a failed username/password.
            this.failures++;

            if (this.failures === 3) {
                // Time to pop up a choice box because they are still having problems.
                let dialog = this.optionDlgSvc.Open({
                    disableClose: true,
                    TitleText: 'Problems Logging In?',
                    MessageText:
                        'You appear to be having some difficulty logging in to the system. Do you require any assistance?',
                    Options: [
                        {
                            DisplayText: 'I forgot my password',
                            Color: 'warn',
                            Response: 'password',
                        },
                        {
                            DisplayText: `No - I'll try again`,
                            Color: 'primary',
                            Response: 'cancel',
                        },
                    ],
                });

                dialog
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((response) => {
                        // Reset the failures back to 0.
                        this.failures = 0;

                        switch (response) {
                            case 'password':
                                this.forgotPassword();
                                break;
                            case 'cancel': // User wishes to cancel and try again.
                                break;
                        }
                    });
            }
        }
    }

    forgotPassword() {
        // this.router.navigate(['forgot-password']);
        this.optionDlgSvc
            .Open({
                TitleText: 'Select Type of User',
                MessageText:
                    'Please click the button for the type of user you are:',
                Options: [
                    {
                        DisplayText: 'Charter Student',
                        Response: 'charter',
                        ClosesDialog: true,
                        Color: 'accent',
                    },
                    {
                        DisplayText: 'Polk County Public School Student/Staff',
                        Response: 'pcsb',
                        ClosesDialog: true,
                    },
                ],
            })
            .subscribe((result) => {
                if (result === 'charter') {
                    // Move the user over to the charter password reset route.
                    this.router.navigate(['password-reset']);
                } else {
                    // Take the user over to the Password Reset Tool.
                    this.confDlgSvc
                        .Open({
                            TitleText: 'Password Reset Tool',
                            MessageText:
                                'You will now be taken to the district Password Reset Tool where you can change your password',
                            OKText: 'OK',
                            CancelText: 'Cancel',
                        })
                        .subscribe((redirect) => {
                            if (!redirect) return;

                            window.location.href =
                                'https://password.mypolkschools.net?from=PEFScholarships';
                        });
                }
            });
    }
}
